import {creditCheck, hasCreditHold, type CreditCheck} from '../../creditCheck';
import {sourceBrandText} from '../../components/sourceBrandText';
import {
  authorNotes,
  authorReasons,
  isLengthNote,
  isSubtitleNote,
  jobError,
} from '../../errors';
import {
  keepsEveryReel,
  mediaReels,
  partialRender,
  previewProgress,
  reelTitle,
  renderBlocker,
  renderBlockerText,
} from '../../reels';
import type {MediaReel, PartialRender, PreviewProgress} from '../../reels';
import type {publicationState} from '../../store/PublicationStore';
import {studioText} from '../../studioText';
import type {Job, JobView, Preview, PreviewReel, Reel} from '../../types';
import {aspectRatio, clipOutputSize, isClippingJob} from '../../videoSource';
import {reviewText as text} from './reviewText';

export type ShareKind = 'learners' | 'course';
export type Sharing = ReturnType<typeof publicationState>;

/** Whether the job can be generated again, and how a failed render is explained. */
export interface RetryState {
  creditCheckPending: boolean;
  /** Why generating waits for the last generation's AI credits to settle; null when it need not. */
  creditCheck: CreditCheck | null;
  /** Generating again cannot start: a credit check, or storyboard problems. */
  retryBlocked: boolean;
  /** The storyboard problems, named by microreel and scene rather than by id. */
  blockerText: string;
  retryReason: string;
  failureReason: string;
}

/** `canAddCredits`: the author may buy AI credits, which a pending credit check says. */
export function retryState(view: JobView, canAddCredits = false): RetryState {
  const {job} = view;
  const reels = job.storyboard?.reels || [];
  const credits = creditCheck(view, canAddCredits);
  const creditCheckPending = !!credits;
  const noun = isClippingJob(job) ? studioText.clipNoun : studioText.sceneNoun;
  const blockerText = authorReasons(view.renderBlockers)
    .map(reason => renderBlockerText(renderBlocker(reels, reason, noun)))
    .join('\n');
  const retryReason = credits ? credits.text : blockerText;
  return {
    creditCheckPending,
    creditCheck: credits,
    retryBlocked: creditCheckPending || view.canRender === false,
    blockerText,
    retryReason,
    failureReason: retryReason || jobError(job.error, job.errorCode),
  };
}

/**
 * A microreel's preview revision. It stays the same while later microreels are added, so the
 * one playing never reloads. Older servers have one revision for the whole preview.
 */
export const previewKey = (preview: Preview | null, reel: PreviewReel) =>
  `${preview?.versionHash}:${
    reel.runtimeVersion || preview?.runtimeVersion || ''
  }:${reel.id}`;

/**
 * Render notes an author can act on; a note that starts with a reel id names the reel. A
 * microreel's length shows in the list, so a note asking to shorten it to its estimate is left
 * out, and so are notes about subtitles.
 */
export function reelWarnings(job: Job): string[] {
  const reels = job.storyboard?.reels || [];
  const notes = (job.warnings || []).filter(
    note => !isLengthNote(note) && !isSubtitleNote(note),
  );
  return authorNotes(notes).map(warning =>
    warning.replace(/^(r\d+):\s*/, (prefix, reelId) => {
      const index = reels.findIndex(r => r.id === reelId);
      return index < 0 ? prefix : `${reelTitle(reels[index], index)}: `;
    }),
  );
}

/** Why the share buttons are disabled; not every browser shows a disabled button's tooltip. */
export function shareHint(
  view: JobView,
  sharing: Pick<Sharing, 'blockers' | 'ready' | 'error' | 'published'>,
): string | undefined {
  const {job} = view;
  const count = job.storyboard?.reels.length || 0;
  const done = job.status === 'done';
  if (job.status === 'rendering' || job.status === 'approved')
    return text.hintGenerating(count);
  if (!done) return text.hintNotGenerated(count);
  if (view.running) return text.hintSaving(count);
  if (sharingWaitsForCredits(view, sharing)) return text.hintCreditCheck;
  if (sharing.blockers.length > 0)
    return job.brief.ending === 'policy_attestation'
      ? text.hintFixAttestation
      : text.hintFixKnowledgeCheck;
  if (!sharing.ready && !sharing.error) return text.hintChecking(count);
  return undefined;
}

/**
 * The server shares a render only once the job's held AI credits settle; a render already
 * shared is shared with learners or a course without sharing it again.
 */
const sharingWaitsForCredits = (
  view: JobView,
  sharing: Pick<Sharing, 'published'>,
) => hasCreditHold(view) && !sharing.published;

/** The phone's states, while Studio drafts the storyboard (see PhoneState). */
export const DRAFTING_STATUSES = [
  'ingesting',
  'planning',
  'checking',
  'choosing_topics',
];

/** The phone frame's shape: landscape for a landscape clip, and the video's own ratio. */
export interface PhoneShape {
  landscape: boolean;
  /** A CSS aspect ratio ("1920 / 1080"); without one the phone is 9 / 16. */
  aspect?: string;
}

/** The selected microreel's page state, worked out from the job and the page's choices. */
export interface ReviewModel {
  view: JobView;
  job: Job;
  reels: Reel[];
  count: number;
  /** The microreels are cut from the video rather than made with narration and visuals. */
  clipping: boolean;
  selectedIndex: number;
  selected?: Reel;
  progress: PreviewProgress;
  /** The finished MP4s, by reel id, once the job is done. */
  files: Record<string, MediaReel>;
  exportReady: boolean;
  selectedFiles?: MediaReel;
  selectedPreview?: PreviewReel;
  /** A clipping microreel's preview is its own MP4: the video player plays it at once. */
  previewIsVideo: boolean;
  previewReady: boolean;
  previewReadyAt?: string;
  size: {width: number; height: number};
  phone: PhoneShape;
  /** The finished MP4 plays rather than the preview. */
  playExport: boolean;
  /** The first microreel that can play, while the selected one is still being made. */
  firstReady: number;
  /** A render that stopped part way keeps the microreels it made. */
  partlyGenerated: boolean;
  /** Which microreels the render that stopped made, and whether generating again keeps them. */
  partial: PartialRender | null;
  /**
   * A render failed after it made every microreel, before it finished them: generating again
   * keeps them all and finishes them. Only newer servers say so (`reusableReels`).
   */
  stoppedAll: boolean;
  warnings: string[];
  retry: RetryState;
  /**
   * A pending credit check needs an alert of its own: neither the phone (for a render that
   * failed or a storyboard changed since it) nor the alert of a render that stopped part way
   * offers what unlocks it, e.g. for a finished render whose credits could not settle.
   */
  creditCheckAlert: boolean;
  share: {
    /** The share buttons are disabled. */
    disabled: boolean;
    /** Knowledge check or attestation problems block sharing. */
    problems: boolean;
    hint?: string;
  };
}

export interface ReviewChoices {
  /** The microreel in the address (?reel=); the first one otherwise. */
  reelId: string | null;
  /** The finished video plays rather than the playable preview. */
  showExport: boolean;
  /** The preview whose page said it can play, by its revision key. */
  loadedPreview: {key: string; readyAt: string} | null;
  sharing: Sharing;
  opening: ShareKind | null;
  /** The author may buy AI credits (see `useAddCreditsPath`). */
  canAddCredits?: boolean;
  savingBrand?: boolean;
}

export function reviewModel(
  view: JobView,
  {
    reelId,
    showExport,
    loadedPreview,
    sharing,
    opening,
    canAddCredits,
    savingBrand = false,
  }: ReviewChoices,
): ReviewModel {
  const {job, media} = view;
  const reels = job.storyboard?.reels || [];
  const count = reels.length;
  const clipping = isClippingJob(job);
  const selectedIndex = Math.max(
    0,
    reels.findIndex(r => r.id === reelId),
  );
  const selected: Reel | undefined = reels[selectedIndex];
  // Each microreel can play as soon as its own preview is ready.
  const progress = previewProgress(view);
  const {preview} = progress;
  const exportReady = !!media && job.status === 'done';
  const files = exportReady ? mediaReels(view) : {};
  const selectedFiles = selected ? files[selected.id] : undefined;
  const selectedPreview = selected ? progress.byReel[selected.id] : undefined;
  const previewIsVideo = selectedPreview?.kind === 'video';
  const previewReady =
    !!selectedPreview &&
    (previewIsVideo ||
      loadedPreview?.key === previewKey(preview, selectedPreview));
  // Clipping microreels keep their video's shape, at the size the server exports them; the
  // others are 1080×1920. The phone frame takes the same shape.
  const size =
    selectedPreview?.width && selectedPreview.height
      ? {width: selectedPreview.width, height: selectedPreview.height}
      : clipping
      ? clipOutputSize(job.sourceVideo?.width, job.sourceVideo?.height)
      : {width: 1080, height: 1920};
  const done = job.status === 'done';
  const problems = done && !view.running && sharing.blockers.length > 0;
  const playExport = !!selectedFiles?.video && (showExport || !selectedPreview);
  // A failed render's previews say which microreels it made; newer servers also say which
  // generating keeps, even once an edit hides them.
  const partial = partialRender(view);
  const partlyGenerated = !!partial;
  const stoppedAll = job.status === 'failed' && keepsEveryReel(view);
  const retry = retryState(view, canAddCredits);
  if (savingBrand) {
    retry.retryBlocked = true;
    retry.retryReason = sourceBrandText.waitForSaving;
  }
  // The phone says why the selected microreel cannot play, with what unlocks a credit check
  // for a render that failed or stopped part way, or a storyboard changed since one.
  const phoneOffersCreditCheck =
    (!selected || (!playExport && !selectedPreview)) &&
    (job.status === 'failed' ||
      !!partial ||
      (job.status !== 'rendering' &&
        !DRAFTING_STATUSES.includes(job.status) &&
        (job.renderCount ?? 0) > 0));
  return {
    view,
    job,
    reels,
    count,
    clipping,
    selectedIndex,
    selected,
    progress,
    files,
    exportReady,
    selectedFiles,
    selectedPreview,
    previewIsVideo,
    previewReady,
    previewReadyAt: previewIsVideo
      ? selectedPreview?.readyAt || preview?.readyAt
      : loadedPreview?.readyAt,
    size,
    phone: {
      landscape: size.width > size.height,
      aspect:
        clipping || previewIsVideo
          ? aspectRatio(size.width, size.height)
          : undefined,
    },
    playExport,
    // While the selected microreel is still generating, the first one ready can play.
    firstReady: reels.findIndex(reel => progress.byReel[reel.id]),
    // Older servers make every preview at once, so theirs never stop part way.
    partlyGenerated,
    partial,
    stoppedAll,
    warnings: reelWarnings(job),
    retry,
    creditCheckAlert:
      !!retry.creditCheck &&
      !((partlyGenerated || stoppedAll) && selectedPreview) &&
      !phoneOffersCreditCheck,
    share: {
      disabled:
        savingBrand ||
        !done ||
        sharing.blockers.length > 0 ||
        !!view.running ||
        sharingWaitsForCredits(view, sharing) ||
        !sharing.ready ||
        sharing.publishing ||
        !!opening,
      problems,
      hint: savingBrand
        ? sourceBrandText.waitForSaving
        : shareHint(view, sharing),
    },
  };
}
